import Decimal from 'decimal.js-light';
import { walletSettings } from '@bz/common';

import { isFiat } from '.';

/**
 * 格式化加密货币显示
 * 显示格式：金额 + 单位 + 币种别名 (例如: 1,232.123K BNB)
 */
function formatCrypto(amount: number | string | Decimal | undefined, currencyCode: string): string {
  try {
    const decimalAmount = new Decimal(amount || 0);
    const precision = currencyConfig[currencyCode]?.precisionUnit;

    return currency.printCurrency(decimalAmount, currencyCode, {
      target: currencyCode,
      precision: precision,
    });
  } catch (error) {
    console.error('Error formatting crypto currency:', error);
    return '0';
  }
}

/**
 * 格式化本地货币显示
 */
function formatLocal(
  amount: number | string | Decimal | undefined,
  currencyCode?: string,
  targetCurrency?: string
): string {
  try {
    const decimalAmount = new Decimal(amount || 0);
    return currency.printCurrency(decimalAmount, currencyCode || walletSettings.showCurrency, {
      target: targetCurrency || walletSettings.showCurrency,
    });
  } catch (error) {
    console.error('Error formatting to local currency:', error);
    return '0';
  }
}

/**
 * 格式化本地货币显示(fiat/crypto有区别)
 * 法币：转为本身货币
 * 加密货币：转为本地法币
 */
function formatFCLocal(amount: number | string | Decimal | undefined, currencyCode: string): string {
  return formatLocal(amount, currencyCode, isFiat(currencyCode) ? currencyCode : undefined);
}

export function formateUsdtNum(amount: number): string {
  return amount?.toFixed(2);
}

/**
 * 格式化日期为本地时区
 * @param time - 时间戳（毫秒）
 * @param hasTime - 是否包含时间部分
 * @returns 格式化后的日期字符串 (YYYY-MM-DD 或 YYYY-MM-DD HH:mm:ss)
 */
export function formatLocalDate(time: number, hasTime: boolean = false) {
  if (!time) return '-';

  const date = new Date(time);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  let result = `${year}-${month}-${day}`;

  if (hasTime) {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    result += ` ${hours}:${minutes}:${seconds}`;
  }

  return result;
}

/**
 * 格式化日期为 UTC 时区
 * @param time - 时间戳（毫秒）
 * @param hasTime - 是否包含时间部分
 * @returns 格式化后的日期字符串 (YYYY-MM-DD 或 YYYY-MM-DD HH:mm:ss)
 */
export function formatUTCDate(time: number, hasTime: boolean = false) {
  if (!time) return '-';

  const date = new Date(time);

  // 强制使用 UTC 时区
  const dateFormatter = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  });

  let result = dateFormatter.format(date);

  if (hasTime) {
    const timeFormatter = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'UTC',
    });
    result += ' ' + timeFormatter.format(date);
  }

  return result;
}
export const formatCurrency = {
  /** 格式化加密货币显示 */
  crypto: formatCrypto,
  /** 格式化法币 */
  fiat: currency.printCurrency.bind(currency),
  /** 格式化本地货币显示(fiat/crypto有区别) */
  fclocal: formatFCLocal,
  /** 格式化本地货币显示 */
  local: formatLocal,
} as const;
